import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './brand-signature.scss';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const BrandSignature = ({ compact = false }) => {
  const logoRef = useRef(null);

  useEffect(() => {
    const svg = logoRef.current;
    const clamp = (value) => Math.min(Math.max(value, 0), 1);
    const createCircle = (radius, attributes) => {
      const circle = document.createElementNS(SVG_NAMESPACE, 'circle');

      circle.setAttribute('cx', 60);
      circle.setAttribute('cy', 60);
      circle.setAttribute('r', radius);
      Object.keys(attributes).forEach((key) => circle.setAttribute(key, attributes[key]));
      svg.appendChild(circle);
    };

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const group = document.createElementNS(SVG_NAMESPACE, 'g');
    const rayCount = 112;
    const inner = 16;
    const lines = [];

    group.setAttribute('stroke-linecap', 'round');
    svg.appendChild(group);

    for (let index = 0; index < rayCount; index += 1) {
      const angle = (index / rayCount) * Math.PI * 2;
      const variation = Math.sin(index * 0.5)
        + 0.7 * Math.sin(index * 0.21 + 1)
        + 0.5 * Math.sin(index * 1.7 + 0.5)
        + 0.4 * Math.sin(index * 3.1);
      const normalizedVariation = clamp((variation + 2.6) / 5.2);
      const line = document.createElementNS(SVG_NAMESPACE, 'line');

      line.setAttribute('x1', (60 + Math.cos(angle) * inner).toFixed(1));
      line.setAttribute('y1', (60 + Math.sin(angle) * inner).toFixed(1));
      group.appendChild(line);
      lines.push({
        element: line,
        cosine: Math.cos(angle),
        sine: Math.sin(angle),
        inner,
        maxLength: 8 + normalizedVariation * 24,
        phase: Math.random() * Math.PI * 2,
        speed: 0.7 + Math.random() * 1.8,
      });
    }

    createCircle(10, {
      fill: 'none', stroke: 'currentColor', 'stroke-width': 1, opacity: 0.55,
    });
    createCircle(2.6, { fill: 'currentColor' });

    let animationFrame;
    let isVisible = true;
    let visibilityObserver;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const renderFrame = (timestamp) => {
      const time = timestamp / 1000;

      lines.forEach((line) => {
        const oscillation = 0.5 + 0.5 * Math.sin(time * line.speed + line.phase);
        const length = 4 + oscillation * line.maxLength;

        line.element.setAttribute(
          'x2',
          (60 + line.cosine * (line.inner + length)).toFixed(1),
        );
        line.element.setAttribute(
          'y2',
          (60 + line.sine * (line.inner + length)).toFixed(1),
        );
        line.element.setAttribute(
          'stroke',
          `hsl(42,54%,${(44 + oscillation * 28).toFixed(0)}%)`,
        );
        line.element.setAttribute('stroke-width', (1.1 + oscillation * 0.8).toFixed(2));
        line.element.setAttribute('opacity', (0.45 + oscillation * 0.55).toFixed(2));
      });
    };

    const animate = (timestamp) => {
      renderFrame(timestamp);
      animationFrame = window.requestAnimationFrame(animate);
    };

    const syncMotionPreference = () => {
      window.cancelAnimationFrame(animationFrame);
      renderFrame(0);

      if (!reducedMotion.matches && isVisible) {
        animationFrame = window.requestAnimationFrame(animate);
      }
    };

    syncMotionPreference();
    if (window.IntersectionObserver) {
      visibilityObserver = new window.IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting;
        syncMotionPreference();
      });
      visibilityObserver.observe(svg);
    }
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', syncMotionPreference);
    } else {
      reducedMotion.addListener(syncMotionPreference);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);
      if (visibilityObserver) visibilityObserver.disconnect();
      if (reducedMotion.removeEventListener) {
        reducedMotion.removeEventListener('change', syncMotionPreference);
      } else {
        reducedMotion.removeListener(syncMotionPreference);
      }
    };
  }, []);

  return (
    <span className={`brand-signature${compact ? ' brand-signature--compact' : ''}`}>
      <svg
        ref={logoRef}
        className="logo-anim brand-signature__mark"
        width={compact ? 34 : 42}
        height={compact ? 34 : 42}
        viewBox="0 0 120 120"
        data-logo="1"
        aria-hidden="true"
        focusable="false"
      />
      {!compact && <span className="brand-signature__name">THEGENIUS ID</span>}
    </span>
  );
};

BrandSignature.propTypes = {
  compact: PropTypes.bool,
};

BrandSignature.defaultProps = {
  compact: false,
};

export default BrandSignature;
